import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CreditCard, ChevronRight, ChevronDown, Check, Coffee, Wallet, Sparkles, ShieldCheck, AlertCircle, Ticket } from "lucide-react";

const YASAMARU_CHEER = "/yasamaru/cheer.png";

export default function BillingSetup() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, tenant } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponValid, setCouponValid] = useState(null); // null | {description, discount_type, discount_value} | "invalid"
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/me").then(({ data }) => {
      setInfo(data);
      if (data?.pending_coupon_code) {
        setCouponCode(data.pending_coupon_code);
        setCouponOpen(true);
        // Auto-validate so the discount banner shows immediately
        api.get(`/coupons/validate/${encodeURIComponent(data.pending_coupon_code)}`)
          .then(({ data: c }) => setCouponValid(c))
          .catch(() => setCouponValid("invalid"));
      }
    }).catch(() => {});
  }, []);

  const validateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponValid(null); return; }
    setCheckingCoupon(true); setErr("");
    try {
      const { data } = await api.get(`/coupons/validate/${encodeURIComponent(code)}`);
      setCouponValid(data);
    } catch (e) {
      setCouponValid("invalid");
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setCheckingCoupon(false); }
  };

  const startCheckout = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { origin: window.location.origin };
      if (couponCode.trim() && couponValid && couponValid !== "invalid") {
        payload.coupon_code = couponCode.trim();
      }
      const { data } = await api.post("/billing/checkout", payload);
      window.location.assign(data.url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const goLater = async () => {
    // If a valid coupon was entered, persist it on the tenant so it auto-applies later
    if (couponCode.trim() && couponValid && couponValid !== "invalid") {
      try { await api.post("/billing/pending-coupon", { coupon_code: couponCode.trim() }); }
      catch (e) { /* non-fatal — fall through */ }
    }
    nav(`/t/${slug}/admin`);
  };

  const active = info?.subscription_status === "active" || info?.subscription_status === "trialing";

  // helper: pretty discount label
  const couponLabel = (c) => {
    if (!c || c === "invalid") return "";
    if (c.discount_type === "free_months") return `${c.discount_value}か月無料`;
    if (c.discount_type === "percent") return `${c.discount_value}% OFF`;
    if (c.discount_type === "amount") return `¥${c.discount_value.toLocaleString()} OFF`;
    return c.description;
  };

  return (
    <div className="min-h-screen bg-soft-mint flex items-center justify-center p-4 relative overflow-hidden">
      <div className="blob bg-emerald-200" style={{ width: 360, height: 360, top: -40, right: -40 }} />
      <div className="w-full max-w-2xl relative z-10">
        <div className="card-soft p-8 md:p-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className="tag bg-emerald-100 text-emerald-800 mb-3"><Sparkles size={12} /> 2週間無料</span>
              <h1 className="font-display text-3xl md:text-4xl font-black text-stone-900">あと一歩で完了です！</h1>
              <p className="text-stone-600 mt-2">{tenant?.name}様、ようこそ。</p>
            </div>
            <img src={YASAMARU_CHEER} alt="やさまる" className="w-24 md:w-28" />
          </div>

          {active ? (
            <div className="mt-6 bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5 text-center" data-testid="billing-active-banner">
              <Check className="text-emerald-500 mx-auto" size={32} />
              <div className="font-display text-xl font-bold text-emerald-700 mt-2">サブスクリプション開始済み</div>
              <button onClick={() => nav(`/t/${slug}/admin`)} className="mt-4 rounded-full bg-stone-900 text-white font-bold px-6 py-2.5">管理画面へ</button>
            </div>
          ) : (
            <>
              <div className="mt-6 rounded-2xl bg-stone-50 border border-stone-200 p-5">
                <div className="text-xs text-stone-500">通常プラン（30名まで一律）</div>
                <div className="flex items-end gap-2 flex-wrap mt-1">
                  <span className="font-display text-4xl font-black text-stone-900">¥1,500</span>
                  <span className="text-stone-500 pb-1">/ 月（税抜）</span>
                </div>
                <div className="text-xs text-emerald-700 font-semibold mt-1">
                  最初の14日間は ¥0（無料お試し）
                  {couponValid && couponValid !== "invalid" && couponValid.discount_type === "free_months" && (
                    <span className="ml-1">＋クーポン {couponValid.discount_value}か月無料 = 合計 約{14 + couponValid.discount_value * 30}日間無料 🎉</span>
                  )}
                </div>
              </div>

              {/* Coupon code (collapsible) */}
              <div className="mt-4 rounded-2xl bg-amber-50/70 border border-amber-200 overflow-hidden">
                <button type="button" onClick={() => setCouponOpen(!couponOpen)} data-testid="coupon-toggle-btn"
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-100/40 transition-colors">
                  <span className="inline-flex items-center gap-2">
                    <Ticket size={14} /> クーポンコードをお持ちですか？
                    {couponValid && couponValid !== "invalid" && (
                      <span className="ml-1 text-[10px] tag bg-emerald-100 text-emerald-800">適用中</span>
                    )}
                  </span>
                  <ChevronDown size={16} className={`transition-transform ${couponOpen ? "rotate-180" : ""}`} />
                </button>
                {couponOpen && (
                  <div className="px-4 pb-4 pt-1">
                    <div className="flex gap-2">
                      <input data-testid="coupon-code-input" type="text" value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }}
                        onBlur={validateCoupon}
                        placeholder="クーポンコードを入力" maxLength={24}
                        inputMode="email" autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                        className="flex-1 px-3.5 py-2.5 rounded-xl border border-amber-200 bg-white outline-none focus:ring-2 focus:ring-amber-300 text-sm font-mono uppercase" />
                      <button onClick={validateCoupon} disabled={checkingCoupon || !couponCode.trim()} data-testid="apply-coupon-btn"
                        className="rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold px-4 text-sm">
                        {checkingCoupon ? "確認中..." : "適用"}
                      </button>
                    </div>
                    {couponValid && couponValid !== "invalid" && (
                      <div className="mt-2 text-xs text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid="coupon-valid">
                        <Check size={12} /> 適用可: {couponValid.description}（{couponLabel(couponValid)}）
                      </div>
                    )}
                    {couponValid === "invalid" && (
                      <div className="mt-2 text-xs text-rose-700" data-testid="coupon-invalid">クーポンコードが無効です</div>
                    )}
                    <div className="mt-2 text-[11px] text-amber-800/80 leading-relaxed">
                      ※ 適用後に「あとで登録」を選んでも、入力したクーポンは保存されます。後日カード登録時に自動で適用されます。
                    </div>
                  </div>
                )}
              </div>

              <ul className="mt-5 space-y-2 text-sm text-stone-700">
                <FeatureRow icon={<Wallet size={14} />}>無料お試し終了後に課金が始まります。途中で解約すれば請求はありません</FeatureRow>
                <FeatureRow icon={<Coffee size={14} />}>カード登録は Stripe の安全なページで行います</FeatureRow>
                <FeatureRow icon={<ShieldCheck size={14} />}>登録後もカード変更・解約・領収書取得はいつでもOK</FeatureRow>
                <FeatureRow icon={<AlertCircle size={14} />}>未登録のままだと打刻・勤怠閲覧・月次レポートはご利用いただけません(スタッフ管理は可能)</FeatureRow>
              </ul>

              {err && <div className="mt-4 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm" data-testid="billing-error">{err}</div>}

              <button onClick={startCheckout} disabled={busy} data-testid="start-checkout-btn"
                className="mt-6 w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold py-3.5 text-lg inline-flex items-center justify-center gap-2">
                <CreditCard size={18} /> {busy ? "Stripeへ移動中..." : "カードを登録して無料お試しを開始"}
              </button>

              <button onClick={goLater} data-testid="skip-billing-btn" className="mt-3 w-full text-stone-500 hover:text-stone-700 text-sm">
                あとで登録する<ChevronRight size={14} className="inline" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, children }) {
  return (
    <li className="flex items-start gap-2 text-stone-700">
      <span className="text-emerald-500 mt-0.5">{icon}</span>
      <span>{children}</span>
    </li>
  );
}
